import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePos } from '../context/PosContext';
import { NAV_ITEMS, NAV_GROUPS, MENU_GATES } from '../navConfig';
import { formatDateTime, formatTime } from '../services/datetime';
import './Dashboard.css';

// Home / dashboard landing — mirrors the Android home screen (content_home.xml
// header clock/day/version + dashboard_icon_layout.xml card grid), reworked as
// a clean web overview: live KPIs from the data layer + quick-access cards.
const sameDay = (iso: string, day: Date) => {
  const d = new Date(iso);
  return d.getFullYear() === day.getFullYear() && d.getMonth() === day.getMonth() && d.getDate() === day.getDate();
};

const Dashboard: React.FC = () => {
  const { config, products, sales, customers, suppliers, formatMoney, isOn } = usePos();
  const [now, setNow] = useState(new Date());

  // Live clock like the Android TextClock header.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const stats = useMemo(() => {
    const today = new Date();
    const completed = sales.filter((s) => s.status === 'completed');
    const todays = completed.filter((s) => sameDay(s.timestamp, today) && s.total > 0);
    const todaySales = todays.reduce((sum, s) => sum + s.total, 0);
    const monthSales = completed
      .filter((s) => {
        const d = new Date(s.timestamp);
        return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && s.total > 0;
      })
      .reduce((sum, s) => sum + s.total, 0);

    const pay = { cash: 0, digital: 0, card: 0, credit: 0 };
    todays.forEach((s) => {
      // Attribute split bills per tender; single-tender bills by their method.
      if (s.payments && s.payments.length > 1) {
        s.payments.forEach((p) => {
          pay[p.method] = (pay[p.method] ?? 0) + p.amount;
        });
      } else {
        pay[s.paymentMethod] = (pay[s.paymentMethod] ?? 0) + s.total;
      }
    });

    const lowStock = products.filter(
      (p) => p.stockable !== false && p.stock <= (p.reorderLevel ?? 10)
    );
    const outstanding = customers.reduce((sum, c) => sum + (c.creditBalance ?? 0), 0);

    return {
      todaySales,
      monthSales,
      billCount: todays.length,
      avgBill: todays.length ? todaySales / todays.length : 0,
      productCount: products.length,
      lowStock,
      customerCount: customers.length,
      supplierCount: suppliers.length,
      outstanding,
      pay,
    };
  }, [sales, products, customers, suppliers]);

  const recentBills = useMemo(
    () =>
      [...sales]
        .filter((s) => s.status === 'completed' && s.total > 0)
        .slice(-6)
        .reverse(),
    [sales]
  );

  // Quick-access cards: the real drawer sections, honoring Menu Option toggles.
  const quickItems = NAV_ITEMS.filter(
    (i) => i.path !== '/dashboard' && (!(i.path in MENU_GATES) || isOn(MENU_GATES[i.path]))
  );

  return (
    <div className="dash">
      {/* header band — store + live clock (content_home.xml) */}
      <div className="dash-header">
        <div>
          <h1>{config.storeName}</h1>
          <span className="dash-sub">Welcome back · v3.5.36 (web)</span>
        </div>
        <div className="dash-clock">{formatDateTime(now)}</div>
      </div>

      {/* KPI cards */}
      <div className="dash-kpis">
        <div className="kpi kpi-teal">
          <span className="kpi-label">Today's Sales</span>
          <span className="kpi-value">{formatMoney(stats.todaySales)}</span>
          <span className="kpi-foot">{stats.billCount} bills · avg {formatMoney(stats.avgBill)}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">This Month</span>
          <span className="kpi-value">{formatMoney(stats.monthSales)}</span>
          <span className="kpi-foot">month to date</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Products</span>
          <span className="kpi-value">{stats.productCount}</span>
          <span className={`kpi-foot ${stats.lowStock.length ? 'warn' : ''}`}>
            {stats.lowStock.length} low / out of stock
          </span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Customers</span>
          <span className="kpi-value">{stats.customerCount}</span>
          <span className={`kpi-foot ${stats.outstanding ? 'warn' : ''}`}>
            {formatMoney(stats.outstanding)} outstanding
          </span>
        </div>
      </div>

      {/* today's payment-mode split */}
      <div className="dash-row">
        <div className="dash-card dash-pay">
          <h2>Today by Payment Mode</h2>
          <div className="pay-grid">
            <div className="pay-cell">
              <span className="pay-name">Cash</span>
              <span className="pay-amt">{formatMoney(stats.pay.cash)}</span>
            </div>
            <div className="pay-cell">
              <span className="pay-name">Digital</span>
              <span className="pay-amt">{formatMoney(stats.pay.digital)}</span>
            </div>
            <div className="pay-cell">
              <span className="pay-name">Card</span>
              <span className="pay-amt">{formatMoney(stats.pay.card)}</span>
            </div>
            <div className="pay-cell">
              <span className="pay-name">Credit</span>
              <span className="pay-amt">{formatMoney(stats.pay.credit)}</span>
            </div>
          </div>
        </div>

        {/* recent bills */}
        <div className="dash-card dash-recent">
          <h2>Recent Bills</h2>
          {recentBills.length === 0 ? (
            <p className="dash-empty">No bills yet. Start one from the Bill screen.</p>
          ) : (
            <ul className="recent-list">
              {recentBills.map((b) => (
                <li key={b.id}>
                  <span className="rb-no">{b.billNo}</span>
                  <span className="rb-cust">{b.customerName || 'Walk-in'}</span>
                  <span className="rb-time">{formatTime(b.timestamp)}</span>
                  <span className="rb-amt">{formatMoney(b.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* low stock alert */}
      {stats.lowStock.length > 0 && (
        <div className="dash-card dash-lowstock">
          <h2>Low / Out of Stock ({stats.lowStock.length})</h2>
          <div className="lowstock-chips">
            {stats.lowStock.slice(0, 12).map((p) => (
              <Link key={p.id} to="/goods-received" className={`ls-chip ${p.stock <= 0 ? 'out' : ''}`}>
                {p.name} · {p.stock}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* quick access grid (dashboard_icon_layout.xml card grid) */}
      <h2 className="dash-section-title">Quick Access</h2>
      {NAV_GROUPS.filter((g) => g !== 'Home').map((group) => {
        const items = quickItems.filter((i) => i.group === group);
        if (items.length === 0) return null;
        return (
          <div className="dash-group" key={group}>
            <div className="dash-group-title">{group}</div>
            <div className="dash-grid">
              {items.map((i) => (
                <Link key={i.path} to={i.path} className="dash-tile">
                  <span className="tile-icon">{i.icon}</span>
                  <span className="tile-label">{i.label}</span>
                </Link>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Dashboard;
